import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getProjects } from '@/api/projects.api';
import { getApiErrorMessage } from '@/api/base.api';
import { DEFAULT_GLOBAL_PROJECT_LIST_PARAMS } from '@/constants/projects.constants';
import type { Project, GetProjectsParams } from '@/interfaces/projects.interface';

const useProjects = (params?: GetProjectsParams) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<{
    total: number;
    page: number;
    size: number;
    pages: number;
  } | null>(null);

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetchProjects = useCallback(async (overrideParams?: GetProjectsParams) => {
    try {
      setLoading(true);
      const paramsToUse =
        overrideParams ??
        paramsRef.current ??
        DEFAULT_GLOBAL_PROJECT_LIST_PARAMS;
      const response = await getProjects(paramsToUse);
      const newProjects = Array.isArray(response.items)
        ? [...response.items]
        : [];
      setProjects(newProjects);
      setPagination({
        total: response.total,
        page: response.page,
        size: response.size,
        pages: response.pages,
      });
      setError(null);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to fetch projects'));
      setProjects([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Only re-fetch when pagination/search/sort primitives change, not when the
  // parent passes a new params object reference with the same values.
  const stableParams = useMemo(
    () => params,
    [params?.page, params?.size, params?.search, params?.sort_by, params?.sort_order]
  );

  useEffect(() => {
    fetchProjects(stableParams);
  }, [stableParams, fetchProjects]);

  return { projects, loading, error, pagination, fetchProjects };
};

export default useProjects;
